(ns metabase.lib-metric.display-info
  "Display info functions for lib_metric entities. Provides UI-friendly
   information about metrics, measures, dimensions, temporal buckets, and
   binning strategies.

   Follows the same multimethod dispatch pattern as metabase.lib.metadata.calculation/display-info."
  (:require
   #?@(:cljs [[metabase.lib.cache :as lib.cache]])
   [clojure.string :as str]
   [metabase.lib-metric.hierarchy :as hierarchy]
   [metabase.lib-metric.operators :as operators]
   [metabase.lib.core :as lib]
   [metabase.lib.metadata.protocols :as lib.metadata.protocols]
   [metabase.lib.schema.temporal-bucketing :as lib.schema.temporal-bucketing]
   [metabase.util :as u]
   [metabase.util.i18n :as i18n]))

;;; -------------------------------------------------- Multimethod --------------------------------------------------

(defmulti display-info-method
  "Implementation for [[display-info]]. Returns a map of UI-friendly information
   for the given entity. Dispatches on `:lib/type` of `x`."
  {:arglists '([definition x])}
  (fn [_definition x]
    (lib/dispatch-value x))
  :hierarchy hierarchy/hierarchy)

;;; -------------------------------------------------- Default Implementation --------------------------------------------------

(defn default-display-info
  "Default implementation that extracts common fields from an entity.
   Used as the base for type-specific implementations. `:long-display-name` defaults
   to `:display-name`."
  [_definition x]
  (let [display-name   (or (:display-name x) (:name x))
        effective-type (:effective-type x)
        semantic-type  (:semantic-type x)]
    (cond-> {}
      display-name              (assoc :display-name display-name
                                       :long-display-name display-name)
      (:name x)                 (assoc :name (:name x))
      effective-type            (assoc :effective-type effective-type)
      semantic-type             (assoc :semantic-type semantic-type)
      (:description x)          (assoc :description (:description x))
      ;; Support both :selected? (from lib/) and :selected (from lib-metric/) naming conventions
      (some? (:selected? x))    (assoc :selected (:selected? x))
      (some? (:selected x))     (assoc :selected (:selected x))
      (some? (:default? x))     (assoc :default (:default? x))
      (some? (:default x))      (assoc :default (:default x)))))

(defmethod display-info-method :default
  [definition x]
  (default-display-info definition x))

;;; Replicates the column-name slugification from metabase.lib.metadata.calculation
;;; so that the frontend can predict the result column name without running a query.
(defn- aggregation-column-name
  [display-name]
  (when (seq display-name)
    (-> display-name
        (str/replace #"[\(\)]" "")
        (u/slugify {:unicode? true}))))

;;; -------------------------------------------------- Metric --------------------------------------------------

(defmethod display-info-method :metadata/metric
  [definition metric]
  (let [display-name (or (:display-name metric)
                         (:name metric))
        shown-name   (or display-name (i18n/tru "Metric"))]
    (merge (default-display-info definition metric)
           ;; Metrics have no group prefix, so the long display name is just the display name.
           ;; Both keys are overridden together because this method supplies a fallback
           ;; `:display-name` — `default-display-info` sets neither when the metric has no name
           ;; at all, and they must not disagree.
           {:display-name      shown-name
            :long-display-name shown-name}
           (when-let [col-name (or (:result-column-name metric)
                                   (some-> display-name aggregation-column-name))]
             {:column-name col-name}))))

;;; -------------------------------------------------- Measure --------------------------------------------------

(defmethod display-info-method :metadata/measure
  [definition measure]
  (let [display-name (or (:display-name measure)
                         (:name measure))
        shown-name   (or display-name (i18n/tru "Measure"))]
    (merge (default-display-info definition measure)
           ;; see the :metadata/metric method for why :long-display-name is overridden too
           {:display-name      shown-name
            :long-display-name shown-name}
           (when-let [col-name (or (:result-column-name measure)
                                   (some-> display-name aggregation-column-name))]
             {:column-name col-name}))))

;;; -------------------------------------------------- Dimension --------------------------------------------------

(defmethod display-info-method :metadata/dimension
  [definition dimension]
  (let [display-name (or (:display-name dimension)
                         (:name dimension)
                         (i18n/tru "Dimension"))
        group        (:group dimension)
        ;; the only display info whose :long-display-name diverges from :display-name — dimensions
        ;; from a connected table are prefixed with the group name
        long-name    (if (and group (not= "main" (:type group)))
                       (str (:display-name group) " → " display-name)
                       display-name)]
    (merge (default-display-info definition dimension)
           {:display-name display-name
            :long-display-name long-name
            :filter-positions (or (:filter-positions dimension) [])
            :projection-positions (or (:projection-positions dimension) [])}
           (when group
             {:group group})
           (when-let [source (:lib/source dimension)]
             {:is-from-join (= source :source/joins)
              :is-calculated (= source :source/expressions)
              :is-implicitly-joinable (= source :source/implicitly-joinable)}))))

;;; -------------------------------------------------- Segment --------------------------------------------------

(defmethod display-info-method :metadata/segment
  [definition segment]
  (cond-> (default-display-info definition segment)
    (:filter-positions segment) (assoc :filter-positions (:filter-positions segment))))

;;; -------------------------------------------------- Temporal Bucket --------------------------------------------------

(defn- temporal-bucket-display-info
  [bucket]
  (let [unit (:unit bucket)]
    {:short-name (u/qualified-name unit)
     :display-name (lib/describe-temporal-unit unit)
     :default (boolean (:default bucket))
     :selected (boolean (:selected bucket))
     :is-temporal-extraction (and (contains? lib.schema.temporal-bucketing/datetime-extraction-units unit)
                                  (not (contains? lib.schema.temporal-bucketing/datetime-truncation-units unit)))}))

(defmethod display-info-method :temporal-bucket
  [_definition bucket]
  (temporal-bucket-display-info bucket))

(defmethod display-info-method :option/temporal-bucketing
  [_definition bucket]
  (temporal-bucket-display-info bucket))

;;; -------------------------------------------------- Binning Strategy --------------------------------------------------

(defn- format-number-simple
  "Format a number for display. Integers as whole numbers, decimals as-is."
  [n]
  (str (if (zero? (rem n 1))
         (int n)
         n)))

(defn- binning-display-name
  "Generate display name for binning options, given a dimension (or nil)."
  [{:keys [bin-width num-bins strategy]} dimension]
  (case strategy
    :num-bins  (i18n/trun "{0} bin" "{0} bins" num-bins)
    :bin-width (str (format-number-simple bin-width)
                    (when (isa? (:semantic-type dimension) :type/Coordinate)
                      "\u00B0"))
    :default   (i18n/tru "Auto binned")))

(defmethod display-info-method :binning-strategy
  [_definition strategy]
  {:display-name (binning-display-name strategy nil)
   :default (boolean (:default strategy))
   :selected (boolean (:selected strategy))})

(defmethod display-info-method :option/binning
  [_definition strategy]
  {:display-name (:display-name strategy)
   :default (boolean (:default strategy))
   :selected (boolean (:selected strategy))})

;;; -------------------------------------------------- MBQL Clauses (Filters) --------------------------------------------------

;; Filter clauses are MBQL vectors like [:= {} [:dimension {} "uuid"] "value"].
;; We look up dimension names from the metadata provider to generate readable descriptions.

(defn- dimension-ref?
  "Check if x is a dimension reference [:dimension opts uuid]."
  [x]
  (and (vector? x)
       (= :dimension (first x))
       (string? (nth x 2 nil))))

(defn- dimension-uuid
  "Extract UUID from a dimension reference [:dimension opts uuid]."
  [dim-ref]
  (nth dim-ref 2))

(defn- lookup-dimension
  "Look up a dimension by UUID from the metadata provider in the definition."
  [definition dimension-id]
  (when-let [mp (:metadata-provider definition)]
    (first (lib.metadata.protocols/metadatas
            mp
            {:lib/type :metadata/dimension :id #{dimension-id}}))))

(defn- dimension-display-name
  "Get the display name for a dimension reference, falling back to UUID if not found."
  [definition dim-ref]
  (let [uuid (dimension-uuid dim-ref)]
    (if-let [dim (lookup-dimension definition uuid)]
      (or (:display-name dim) (:name dim) uuid)
      uuid)))

(defn- format-filter-value
  "Format a filter value for display."
  [v]
  (cond
    (string? v) v
    (number? v) (str v)
    (boolean? v) (str v)
    (nil? v) "empty"
    :else (str v)))

(defn- operator-display-name
  "Get a human-readable name for a filter operator."
  [op]
  (operators/display-name op))

(defn- describe-temporal-interval
  "Describe a temporal interval as human-readable text.
   n can be :current, 0, a negative int (past), or a positive int (future)."
  [n unit]
  (let [unit-name (name unit)]
    (cond
      (or (= n :current) (= n 0))
      (i18n/tru "the current {0}" unit-name)

      (and (number? n) (neg? n))
      (let [abs-n (abs n)]
        (i18n/tru "in the last {0} {1}s" abs-n unit-name))

      (and (number? n) (pos? n))
      (i18n/tru "in the next {0} {1}s" n unit-name)

      :else
      (str n " " unit-name))))

(defn- filter-clause-display-name
  "Generate a display name for a filter clause."
  [definition [op _opts & args]]
  (let [first-arg (first args)
        dim-name (when (dimension-ref? first-arg)
                   (dimension-display-name definition first-arg))
        op-name (operator-display-name op)]
    (case op
      ;; Unary operators (no value)
      (:is-null :not-null :is-empty :not-empty)
      (if dim-name
        (i18n/tru "{0} {1}" dim-name op-name)
        op-name)

      ;; Between operator (two values)
      :between
      (let [[_ v1 v2] args]
        (if dim-name
          (i18n/tru "{0} {1} {2} and {3}" dim-name op-name (format-filter-value v1) (format-filter-value v2))
          (i18n/tru "{0} {1} and {2}" op-name (format-filter-value v1) (format-filter-value v2))))

      ;; Relative date filters
      :time-interval
      (let [[dim-ref n unit] args
            dim-name (when (dimension-ref? dim-ref)
                       (dimension-display-name definition dim-ref))
            interval-desc (describe-temporal-interval n unit)]
        (if dim-name
          (i18n/tru "{0} is {1}" dim-name interval-desc)
          interval-desc))

      ;; Default: binary/varargs operators
      (let [values (rest args)
            formatted-values (map format-filter-value values)]
        (if dim-name
          (if (= 1 (count values))
            (i18n/tru "{0} {1} {2}" dim-name op-name (first formatted-values))
            (i18n/tru "{0} {1} {2} values" dim-name op-name (count values)))
          (if (= 1 (count values))
            (i18n/tru "{0} {1}" op-name (first formatted-values))
            (i18n/tru "{0} {1} values" op-name (count values))))))))

(defn- filter-clause-display-info
  "Generate display info for an MBQL filter clause."
  [definition clause]
  {:display-name (filter-clause-display-name definition clause)})

;; Filter operators derive from operators/::filter-operator via the shared hierarchy.
;; The derivations are registered in metabase.lib-metric.operators when it loads.
(defmethod display-info-method ::operators/filter-operator
  [definition clause]
  (filter-clause-display-info definition clause))

;;; -------------------------------------------------- Public API --------------------------------------------------

(defn display-info
  "Get display info for a displayable item. Returns a map suitable for UI consumption.

   Dispatches on `:lib/type` of `x` to return appropriate display info structure.
   Results are cached in CLJS for performance."
  [definition x]
  (letfn [(display-info* [x]
            (try
              (display-info-method definition x)
              (catch #?(:clj Throwable :cljs js/Error) e
                (throw (ex-info (i18n/tru "Error calculating display info for {0}: {1}"
                                          (lib/dispatch-value x)
                                          (ex-message e))
                                {:definition definition :x x}
                                e)))))]
    #?(:clj
       (display-info* x)
       :cljs
       (lib.cache/side-channel-cache
        :display-info x
        display-info*))))
